import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";

import Header from "../../components/header/Header";
import LoadingSpinner from "../../components/shared/ui/Spinner";
import { TableHisLogs } from "../../components/table-activity/TableActivityHisLogs/TableHisLogs";
import { TableDeviceLogs } from "../../components/table-activity/TableActivityDeviceLogs/TableDeviceLogs";
import { TableUserActionsLogs } from "../../components/table-activity/TableUserActionsLogs/TableUserActionsLogs";
import { useActivity } from "../../context/ActivityProvider";

export default function ActivityLog() {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { t } = useTranslation();
    const {
        offset,
        limit,
        setOffset,
        setLimit,
        activityList,
        activityLoading,
        getActivityList,
    } = useActivity();

    const [activeTab, setActiveTab] = useState("");

    const i18nActivityDirectory = (key: string): string =>
        t(`activity-directory.${key}`);

    const tabs = useMemo(
        () => [
            { id: "his-logs", label: i18nActivityDirectory("his") },
            { id: "device-logs", label: i18nActivityDirectory("device") },
            { id: "menu-item", label: i18nActivityDirectory("menu") },
        ],
        [t]
    );

    const selectTab = (id: string) => {
        setActiveTab(id);
        navigate("/activitylog/", { state: { slug: id } });
        getActivityList({ tabId: id });
        setLimit(10);
        setOffset(0)
    };

    useEffect(() => {
        if (state?.slug) selectTab(state.slug);
    }, [state?.slug]);

    useEffect(() => {
        if (activeTab) getActivityList({ tabId: activeTab });
    }, [limit, offset, activeTab]);

    const tableProps = useMemo(
        () => ({
            offset,
            setOffset,
            setLimit,
            activityList,
            limit,
            filterNotFound: false,
        }),
        [offset, setOffset, setLimit, activityList, limit]
    );

    const tabComponents: Record<string, JSX.Element> = {
        "his-logs": <TableHisLogs {...tableProps} />,
        "device-logs": <TableDeviceLogs {...tableProps} />,
        "menu-item": <TableUserActionsLogs {...tableProps} />,
    };

    return (
        <div className="relative h-screen flex flex-col overflow-hidden">
            <Header title={i18nActivityDirectory("activity-log")} />

            <div className="flex-1 flex relative overflow-hidden">
                <div className="flex flex-col w-full transition-all duration-500 ease-in-out overflow-hidden">

                    <div className="flex-shrink-0 w-full p-4 bg-white border-b">
                        <div className="flex w-full justify-between items-center">
                            <div className="h-auto">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => selectTab(tab.id)}
                                        className={`flex-1 p-2 text-sm font-medium transition-colors ${activeTab === tab.id
                                            ? "text-primeblue border-b-2 border-primeblue"
                                            : "text-primary-light"
                                            }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            {/* <FilterButton
                                disabled
                                icon="filter_list"
                                label={i18nActivityDirectory("filters")}
                            /> */}
                        </div>
                    </div>

                    {activityLoading ? (
                        <div className="flex-1 flex items-center justify-center">
                            <LoadingSpinner size="lg" text="Loading activity..." />
                        </div>
                    ) : (
                        <div className="flex-1 m-4 overflow-auto">
                            {tabComponents[activeTab] || null}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
